/**
 * QrCameraScanner — abre la cámara del dispositivo y detecta códigos QR.
 * Adaptado del componente del monorepo kpop para el PWA de taquilla
 * pagafacil.
 *
 * Dos motores de detección:
 *   1. BarcodeDetector API — nativa Chrome/Edge desktop, Chrome/Samsung
 *      Internet Android. Más eficiente en batería.
 *   2. jsQR — fallback JS puro para Safari iOS/iPad y cualquier browser
 *      con getUserMedia. ~9 KB gzipped.
 *
 * Uso:
 *   {show && <QrCameraScanner onScan={(t) => doCheckin(t)} onClose={close} />}
 *
 * onScan recibe el rawValue del QR. Si es una URL con `/validate/<token>`
 * (formato que emite generate_qr_for_order de pagafacil), extraemos el
 * token automáticamente antes de llamar onScan.
 */
import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';

interface Props {
  onScan: (token: string) => void;
  onClose: () => void;
  extractTokenFromUrl?: boolean;
}

declare global {
  interface Window {
    BarcodeDetector?: {
      new (opts?: { formats?: string[] }): {
        detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
      };
      getSupportedFormats?: () => Promise<string[]>;
    };
  }
}

export default function QrCameraScanner({ onScan, onClose, extractTokenFromUrl = true }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [engine, setEngine] = useState<'native' | 'jsqr' | null>(null);

  useEffect(() => {
    let cancelled = false;

    const handleDetection = (raw: string) => {
      const token = extractTokenFromUrl ? extract(raw.trim()) : raw.trim();
      if (token) onScan(token);
    };

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e: any) {
        setError(e?.message || 'No se pudo acceder a la cámara');
        return;
      }

      const hasNative = 'BarcodeDetector' in window && !!window.BarcodeDetector;
      if (hasNative) {
        setEngine('native');
        try {
          const detector = new window.BarcodeDetector!({ formats: ['qr_code'] });
          const tick = async () => {
            if (cancelled || !videoRef.current) return;
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes && codes.length > 0) {
                handleDetection(codes[0].rawValue);
                return;
              }
            } catch { /* engine flaky, ignore this frame */ }
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
          return;
        } catch { /* falls through to jsQR */ }
      }

      // Fallback jsQR (Safari iOS/iPad)
      setEngine('jsqr');
      const tick = () => {
        if (cancelled || !videoRef.current || !canvasRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx || video.readyState < 2) {
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w && h) {
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(video, 0, 0, w, h);
          const img = ctx.getImageData(0, 0, w, h);
          const code = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
          if (code?.data) {
            handleDetection(code.data);
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    void start();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [onScan, extractTokenFromUrl]);

  return (
    <div style={overlayStyle}>
      <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <button onClick={onClose} style={closeBtnStyle}>× Cerrar</button>
      </div>

      <div style={{ position: 'relative', width: '90%', maxWidth: 480, aspectRatio: '1 / 1', border: '3px solid #E8B44A', borderRadius: 20, overflow: 'hidden', background: '#000' }}>
        <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {/* Overlay guía visual */}
        <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 3px rgba(232,180,74,0.4)', pointerEvents: 'none' }} />
        {!ready && !error && (
          <div style={centerHint}>Pidiendo cámara…</div>
        )}
        {error && (
          <div style={centerHint}>
            <div style={{ color: '#FCA5A5', marginBottom: 8 }}>❌ {error}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Cierra y usa entrada manual.</div>
          </div>
        )}
      </div>

      <div style={{ color: '#F7F2E9', marginTop: 20, fontSize: 14, opacity: 0.9, textAlign: 'center', padding: '0 24px' }}>
        Encuadra el QR de la entrada dentro del recuadro
        {engine && <span style={{ display: 'block', opacity: 0.5, fontSize: 11, marginTop: 6 }}>{engine === 'native' ? 'motor nativo' : 'motor JS'}</span>}
      </div>

      {/* Canvas oculto para el fallback jsQR */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

function extract(raw: string): string {
  const m = raw.match(/(?:validate|checkin)\/([A-Za-z0-9_-]+)/);
  if (m) return m[1];
  return raw;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.92)',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const closeBtnStyle: React.CSSProperties = {
  background: '#F7F2E9', color: '#1E2A2E', border: 'none',
  padding: '10px 18px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
};

const centerHint: React.CSSProperties = {
  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.7)', color: '#F7F2E9', textAlign: 'center', padding: 20,
};
